import React, {FC, PropsWithChildren, useCallback, useMemo} from 'react';
import {Pressable, StyleProp, View, ViewStyle} from 'react-native';
import {ALIGN, ButtonIcon, COLOR, DISPLAY, IconName, Text, useDevice} from '@lookiero/aurora';
import {style as footerStyle} from './Footer.style';
import {matchPath, useLocation} from 'react-router-native';
import {Path} from '@modules/routing/routes.definition';

interface FooterItem extends PropsWithChildren {
  readonly icon: IconName;
  readonly label: string;
  readonly path: Path;
  readonly notification?: boolean;
  readonly testID?: string;
  readonly hidden?: boolean;
  readonly style?: StyleProp<ViewStyle>;
}

interface FooterProps {
  readonly data: FooterItem[];
  readonly style?: StyleProp<ViewStyle>;
  readonly onChange: (path: Path) => void;
}

const Footer: FC<FooterProps> = ({data, onChange, style: customStyle}) => {
  const {pathname} = useLocation();
  const {screen} = useDevice();
  const style = useMemo(() => footerStyle(), []);

  const isActive = useCallback((path: string) => matchPath(`${path}/*`, pathname), [pathname]);

  return (
    <View style={[style.footer, !screen.L && style.footerSmall, customStyle]} testID="footer">
      {data.map(
        ({icon, label, path, notification, testID, children, hidden, style: footerItemCustomStyle}) =>
          !hidden && (
            <Pressable
              key={path}
              testID={testID}
              style={[style.button, screen.L && style.buttonDesktop, footerItemCustomStyle]}
              onPress={() => onChange(path)}
            >
              <View>
                <ButtonIcon
                  color={isActive(path) ? COLOR.ICON : COLOR.ICON_OPACITY_LIGHT}
                  name={icon}
                  notification={notification}
                  style={[style.iconContainer, screen.L && style.iconDesktop]}
                  small
                />
              </View>

              <Text
                align={ALIGN.CENTER}
                color={isActive(path) ? COLOR.TEXT : COLOR.TEXT_OPACITY}
                detail={!screen.L}
                display={DISPLAY.BLOCK}
                ellipsizeMode
                action={screen.L}
                level={3}
                style={[style.text]}
              >
                {label}
              </Text>

              {children}
            </Pressable>
          ),
      )}
    </View>
  );
};

export type {FooterItem};
export {Footer};
